import { useQuery } from "@tanstack/react-query";
import { MultiLineChart } from "../charts/dashboard-chart";
import { getInfluxMultiDeviceOverview } from "../../api/axios";
import { Spinner } from "../ui/spinner";
import { ErrorMessage } from "../ui/error-message";
import { useEffect, useState } from "react";
import { normalizeDeviceMetrics } from '../../lib/utils';

const DEVICE_COLORS = [
  "#1a56db", // blue
  "#10b981", // green
  "#f97316", // orange
  "#8b5cf6", // purple
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#f59e0b", // amber
  "#ef4444", // red
];

interface PerformanceChartsProps {
  timeRange: string;
  selectedDeviceId?: string; // optional filter for a single device
}

export default function PerformanceCharts({ timeRange, selectedDeviceId }: PerformanceChartsProps) {
  const { data: influxOverviewData, isLoading: isLoadingInfluxData, error: influxError } = useQuery({
    queryKey: ["influx-multi-device", timeRange],
    queryFn: async () => {
      const startParam = getTimeRangeForInflux(timeRange);
      console.log(`Fetching performance data with time range: ${timeRange} -> ${startParam}`);
      const data = await getInfluxMultiDeviceOverview({ start: startParam });
      console.log(`Received ${data?.devices?.length || 0} devices with time range ${startParam}`);
      return data;
    },
    enabled: !!timeRange,
    retry: 2,
  });

  const normalizeDevices = (devices: any[]) => {
    return devices.map((device) => {
      const normalizedMetrics = normalizeDeviceMetrics({
        cpuLoad: device.cpuLoad,
        memUsed: device.memUsed,
        memTotal: device.memTotal,
        diskUtil: device.diskUtil,
      });

      return {
        ...device,
        cpuLoad: normalizedMetrics.cpuLoad,
        memUsedPercent: normalizedMetrics.memUsedPercent,
        diskUtil: normalizedMetrics.diskUtil,
      };
    });
  };

  const prepareTimeSeriesDatasets = (
    metricType: "cpuLoad" | "memUsed" | "networkTraffic" | "diskUtil",
    filterDeviceType?: string
  ) => {
    if (!influxOverviewData || !Array.isArray(influxOverviewData.devices) || influxOverviewData.devices.length === 0) {
      return [];
    }

    const normalizedDevices = normalizeDevices(influxOverviewData.devices);

    const deviceNames = new Map<string, string>();
    normalizedDevices.forEach(
      (
        device: {
          deviceId?: string | number;
          deviceName?: string;
          name?: string;
          type?: string;
          [key: string]: any;
        },
        index: number
      ) => {
        const deviceKey =
          (device.deviceId != null ? device.deviceId.toString() : undefined) ??
          `index-${index}`;
        const name = device.deviceName || device.name || `Device ${deviceKey}`;
        deviceNames.set(deviceKey, name);
      }
    );

    return normalizedDevices
      .filter(
        (
          device: {
            deviceId?: string | number;
            type?: string;
            [key: string]: any;
          },
          index: number
        ) => {
          const deviceKey =
            (device.deviceId != null ? device.deviceId.toString() : undefined) ??
            `index-${index}`;
          const matchesType =
            !filterDeviceType || device.type === filterDeviceType;
          const matchesId =
            !selectedDeviceId ||
            deviceKey === selectedDeviceId.toString();
          return matchesType && matchesId;
        }
      )
      .map(
        (
          device: {
            deviceId?: string | number;
            deviceName?: string;
            name?: string;
            timeSeries?: any[];
            lastUpdate?: string;
            memTotal?: number;
            memUsed?: number;
            cpuLoad?: number;
            diskUtil?: number;
            [key: string]: any;
          },
          index: number
        ) => {
          const color = DEVICE_COLORS[index % DEVICE_COLORS.length];
          const deviceKey =
            (device.deviceId != null ? device.deviceId.toString() : undefined) ??
            `index-${index}`;
          const deviceName =
            deviceNames.get(deviceKey) || `Device ${deviceKey}`;

        // Process time series data from the backend
        const timeSeriesData = (device.timeSeries || []).map((dataPoint: any) => {
          const memUsedPercent = memoryUsedPercent(dataPoint);

          // Format time label based on time range
          let timeLabel = "Unknown";
          if (dataPoint.timestamp) {
            const date = new Date(dataPoint.timestamp);
            
            // For 7 days and 30 days, include the date
            if (timeRange === "last7days" || timeRange === "last30days") {
              timeLabel = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
            } else {
              // For 24 hours, just show time
              timeLabel = date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              });
            }
          }

          return {
            time: timeLabel,
            timestamp: dataPoint.timestamp,
            value: metricType === "cpuLoad"
              ? dataPoint.cpuLoad ?? 0
              : metricType === "memUsed"
              ? memUsedPercent
              : metricType === "diskUtil"
              ? dataPoint.diskUtil ?? 0
              : metricType === "networkTraffic"
              ? dataPoint.networkTraffic ?? 0
              : 0,
          };
        });

        // If no time series data, fall back to single point
        if (timeSeriesData.length === 0) {
          const memUsedPercent = memoryUsedPercent(device);

          // Format time label based on time range
          let timeLabel = "Now";
          if (device.lastUpdate) {
            const date = new Date(device.lastUpdate);
            
            if (timeRange === "last7days" || timeRange === "last30days") {
              timeLabel = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
            } else {
              timeLabel = date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              });
            }
          }

          timeSeriesData.push({
            time: timeLabel,
            timestamp: device.lastUpdate,
            value: metricType === "cpuLoad"
              ? device.cpuLoad ?? 0
              : metricType === "memUsed"
              ? memUsedPercent
              : metricType === "diskUtil"
              ? device.diskUtil ?? 0
              : metricType === "networkTraffic"
              ? device.networkTraffic ?? 0
              : 0,
          });
        }

        return {
          deviceId: deviceKey,
          deviceName,
          color,
          data: timeSeriesData,
        };
      });
  };

  const isLoading = isLoadingInfluxData;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (influxError) {
      setError("Failed to load performance data. Please try again later.");
    } else {
      setError(null);
    }
  }, [influxError]);

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-lg leading-6 font-medium text-neutral-dark mb-4">
          System Performance
        </h2>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <ErrorMessage message={error} />
      </div>
    );
  }

  const hasInfluxData = influxOverviewData && Array.isArray(influxOverviewData.devices) && influxOverviewData.devices.length > 0;

  if (!hasInfluxData) {
    return (
      <div className="mb-8">
        <h2 className="text-lg leading-6 font-medium text-neutral-dark mb-4">
          System Performance
        </h2>
        <p>No performance data available.</p>
      </div>
    );
  }

  const cpuDatasets = prepareTimeSeriesDatasets("cpuLoad");
  const memoryDatasets = prepareTimeSeriesDatasets("memUsed");
  const networkDatasets = prepareTimeSeriesDatasets("networkTraffic");

  // Debug logging
  console.log('InfluxDB Overview Data:', influxOverviewData);
  console.log('CPU Datasets:', cpuDatasets);
  console.log('Memory Datasets:', memoryDatasets);
  console.log('Network Datasets:', networkDatasets);

  return (
    <div className="mb-8">
      <h2 className="text-lg leading-6 font-medium text-neutral-dark mb-4">
        System Performance
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <MultiLineChart title="CPU Utilization" datasets={cpuDatasets} yAxisLabel="%" />
        <MultiLineChart title="Memory Usage" datasets={memoryDatasets} yAxisLabel="%" />
        <MultiLineChart title="Network Traffic" datasets={networkDatasets} yAxisLabel="Mbps" />
      </div>
    </div>
  );
}

function memoryUsedPercent(point: {
  memTotal?: number | null;
  memUsed?: number | null;
  memFree?: number | null;
}): number {
  const total = Number(point.memTotal);
  const used = Number(point.memUsed);
  const free = Number(point.memFree);
  if (Number.isFinite(total) && total > 0 && Number.isFinite(used)) {
    return Math.min(100, Math.max(0, (used / total) * 100));
  }
  if (Number.isFinite(total) && total > 0 && Number.isFinite(free)) {
    return Math.min(100, Math.max(0, ((total - free) / total) * 100));
  }
  return 0;
}

function getTimeRangeForInflux(timeRange: string): string {
  switch (timeRange) {
    case "last24hours":
      return "-24h";
    case "last7days":
      return "-7d";
    case "last30days":
      return "-30d";
    case "custom":
      return "-24h"; // Default to 24h for custom range
    default:
      return "-24h";
  }
}
